import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Phone } from 'lucide-react';
import careersHeroImage from '../assets/careers-hero.jpg';

const Careers = () => {
  const breadcrumbs = [
    { label: 'Home', link: '/' },
    { label: 'Careers', link: null }
  ];

  const benefits = [
    {
      title: 'Purposeful Work',
      description: 'Create genuine impact in the lives of those we care for and their families each day.'
    },
    {
      title: 'Professional Development',
      description: 'Access continuous training, skill enhancement, and clear pathways for career progression.'
    },
    {
      title: 'Collaborative Environment',
      description: 'Join a dedicated team of professionals who value compassionate, person-centred care.'
    },
    {
      title: 'Attractive Package',
      description: 'Receive competitive remuneration, superannuation, professional development support, and leave entitlements.'
    }
  ];

  const openPositions = [
    {
      title: 'Healthcare Assistant',
      department: 'Care Services',
      type: 'Full-Time',
      location: 'Weston, ACT',
      description: 'Become part of our care team as a Healthcare Assistant, supporting clients with daily activities and personal care. You will deliver compassionate assistance, help with meals and mobility, and foster a warm, respectful environment.',
      link: '/jobs/healthcare-assistant'
    },
    {
      title: 'Physiotherapist',
      department: 'Allied Health',
      type: 'Full-Time / Part-Time',
      location: 'Weston, ACT',
      description: 'Join our allied health team to provide professional physiotherapy services to our clients. You will assess mobility needs, develop treatment plans, and work collaboratively to enhance quality of life and independence.',
      link: '/jobs/physiotherapist'
    }
    // Driver role temporarily removed from listings.
    // ,{
    //   title: 'Driver',
    //   department: 'Support Services',
    //   type: 'Casual / Part-Time',
    //   location: 'Weston, ACT',
    //   description: 'Support our clients by providing safe, reliable transport to appointments, social activities, and community events. You will ensure comfortable journeys and contribute to maintaining independence and social connection.',
    //   link: '/jobs/driver'
    // }
  ];

  const values = [
    'Compassionate and client-focused approach to care',
    'Excellent communication and interpersonal abilities',
    'Dedication to dignity, respect, and client wellbeing',
    'Capacity to work effectively within a team',
    'Relevant qualifications and experience in your discipline',
    'Enthusiasm for creating positive change'
  ];

  const pad = (n) => String(n + 1).padStart(2, '0');

  return (
    <div className="min-h-screen bg-white">
      {/* Hero: editorial, left-aligned with a meta rail */}
      <section className="bg-white pt-12 md:pt-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-sm text-bnBody mb-12">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="text-bnBorder">/</span>}
                {crumb.link ? (
                  <Link to={crumb.link} className="hover:text-bnAccent transition-colors">{crumb.label}</Link>
                ) : (
                  <span className="text-bnInk">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-8">
              <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-6">
                Join Our Team
              </p>
              <h1 className="font-display font-light text-bnInk leading-[1.02] text-5xl md:text-6xl lg:text-7xl tracking-tight">
                Build a fulfilling<br />career in care.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-bnBody text-base md:text-lg leading-relaxed mb-8">
                We are looking for dedicated, caring professionals to join B &amp; N Enterprise, where your
                work makes a genuine difference every day.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#positions" className="inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors">
                  View Open Positions <ArrowRight size={16} />
                </a>
                <a href="tel:+61480804640" className="inline-flex items-center gap-2 border border-bnBorder text-bnInk hover:border-bnAccent hover:text-bnAccent font-medium text-sm px-6 py-3.5 rounded-full transition-colors">
                  <Phone size={16} /> +61 480 804 640
                </a>
              </div>
            </div>
          </div>

          {/* Meta strip */}
          <div className="mt-14 border-t border-bnBorder pt-6 flex flex-wrap gap-x-14 gap-y-4">
            {[
              ['Location', 'Weston, ACT'],
              ['Open roles', String(openPositions.length).padStart(2, '0')],
              ['Employment', 'Full-time & part-time'],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-bnAccent text-[11px] tracking-[0.2em] uppercase mb-1">{k}</p>
                <p className="text-bnInk text-sm md:text-base">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cinematic image band */}
      <section className="pt-12 md:pt-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overflow-hidden rounded-3xl">
            <img src={careersHeroImage} alt="The care team at B & N Enterprise" className="w-full h-64 md:h-[420px] object-cover" />
          </div>
        </div>
      </section>

      {/* Why work with us: numbered editorial list */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
                Why Choose B &amp; N Enterprise
              </p>
              <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl">
                More than just employment
              </h2>
            </div>
            <div className="lg:col-span-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="grid grid-cols-[auto,1fr] gap-6 md:gap-10 py-7 border-t border-bnBorder last:border-b">
                  <span className="font-display font-light text-bnAccent text-3xl md:text-4xl leading-none w-12">
                    {pad(index)}
                  </span>
                  <div>
                    <h3 className="text-bnInk font-medium text-lg md:text-xl mb-1.5">{benefit.title}</h3>
                    <p className="text-bnBody text-sm md:text-base leading-relaxed max-w-xl">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open positions: typographic index */}
      <section id="positions" className="bg-bnCream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
                Current Opportunities
              </p>
              <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl lg:text-5xl">
                Open positions
              </h2>
            </div>
            <p className="text-bnBody text-sm md:text-base max-w-sm">
              Browse our current vacancies and find the role that fits your skills and aspirations.
            </p>
          </div>

          <div className="border-t border-bnBorder">
            {openPositions.map((position, index) => (
              <Link
                key={index}
                to={position.link}
                className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center py-8 md:py-10 border-b border-bnBorder transition-colors hover:bg-white/60 -mx-4 md:-mx-6 px-4 md:px-6 rounded-2xl"
              >
                <span className="md:col-span-1 font-display font-light text-bnAccent text-2xl md:text-3xl leading-none">
                  {pad(index)}
                </span>
                <div className="md:col-span-7">
                  <h3 className="font-display font-normal text-bnInk text-2xl md:text-3xl leading-tight mb-2 group-hover:text-bnAccent transition-colors">
                    {position.title}
                  </h3>
                  <p className="text-bnBody text-sm md:text-base leading-relaxed max-w-xl">
                    {position.description}
                  </p>
                </div>
                <div className="md:col-span-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span className="text-bnInk">{position.department}</span>
                  <span className="text-bnBody">{position.type}</span>
                  <span className="text-bnBody">{position.location}</span>
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-bnBorder text-bnInk group-hover:bg-bnAccent group-hover:border-bnAccent group-hover:text-white transition-colors">
                    <ArrowUpRight size={20} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Values: two-column editorial */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              What We Value
            </p>
            <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl mb-5">
              The attributes that define our team
            </h2>
            <p className="text-bnBody text-base md:text-lg leading-relaxed">
              We seek individuals who embody our values and commitment to outstanding care.
            </p>
          </div>
          <div className="lg:col-span-7">
            {values.map((value, index) => (
              <div key={index} className="flex items-baseline gap-5 py-4 border-t border-bnBorder last:border-b">
                <span className="text-bnAccent text-xs tracking-[0.2em] font-medium w-8 shrink-0">{pad(index)}</span>
                <span className="text-bnInk text-base md:text-lg leading-relaxed">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA: dark editorial band */}
      <section id="apply" className="bg-bnInk py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-6">
                Start Here
              </p>
              <h2 className="font-display font-light text-white leading-[1.05] text-4xl md:text-5xl lg:text-6xl">
                Ready to begin your journey with us?
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-white/70 text-base leading-relaxed mb-8">
                Join a team that champions compassion, professionalism, and making a genuine difference
                in people's lives.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#positions" className="inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors">
                  View Open Positions <ArrowRight size={16} />
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-white/25 text-white hover:border-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors">
                  Contact Our Team <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
